import type { PersistStorage, StorageValue } from "zustand/middleware";

type TtlStorageOptions = {
    ttlMs: number;
    /** Top-level state keys that survive expiry. */
    preserveKeys?: string[];
};

/**
 * localStorage-backed persist storage whose entry expires `ttlMs` after its
 * last write. On expiry everything except `preserveKeys` is dropped and the
 * trimmed entry is written back immediately, so stale data does not linger
 * until the next state change.
 *
 * Returns a no-op storage on the server: localStorage is browser-only, and
 * Node 22's experimental Web Storage warns when it is touched.
 */
export function createTtlStorage<S extends Record<string, unknown>>({
    ttlMs,
    preserveKeys = [],
}: TtlStorageOptions): PersistStorage<S> {
    if (typeof window === "undefined") {
        return { getItem: () => null, setItem: () => {}, removeItem: () => {} };
    }

    const tsKey = (name: string) => `${name}_ts`;

    const setItem = (name: string, value: StorageValue<S>) => {
        localStorage.setItem(name, JSON.stringify(value));
        localStorage.setItem(tsKey(name), String(Date.now()));
    };

    const removeItem = (name: string) => {
        localStorage.removeItem(name);
        localStorage.removeItem(tsKey(name));
    };

    return {
        setItem,
        removeItem,
        getItem: (name) => {
            const raw = localStorage.getItem(name);
            if (!raw) return null;

            let value: StorageValue<S>;
            try {
                value = JSON.parse(raw);
            } catch {
                removeItem(name);
                return null;
            }

            const savedAt = Number(localStorage.getItem(tsKey(name)));
            const expired = savedAt && Date.now() - savedAt > ttlMs;
            if (!expired) return value;

            const preserved = Object.fromEntries(
                preserveKeys
                    .filter((key) => value.state?.[key] !== undefined)
                    .map((key) => [key, value.state[key]]),
            ) as S;
            const trimmed = { ...value, state: preserved };
            setItem(name, trimmed);
            return trimmed;
        },
    };
}
